<script setup>
import Section from "@/widgets/profile-drawer/ui/internal/Section.vue";
import EditableField from "@/widgets/profile-drawer/ui/fields/EditableField.vue";
import ReadonlyField from "@/widgets/profile-drawer/ui/fields/ReadonlyField.vue";

defineProps({
  data: { type: Object, required: true },
  editMode: { type: Boolean, default: false },
  isOwn: { type: Boolean, default: false },
  organizations: { type: Array, default: () => [] },
  roles: { type: Array, default: () => [] },
  lockUserFields: { type: Boolean, default: false },
});

const emit = defineEmits(["update"]);

function setField(key, value) {
  emit("update", key, value);
}
</script>

<template>
  <div
    class="grid gap-4 grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] max-[1100px]:grid-cols-1"
  >
    <div class="flex flex-col">
      <Section
        plain
        :title="$t('Основные данные')"
        :sub="$t('ФИО, идентификатор и дата рождения')"
      >
        <div class="grid grid-cols-2 gap-x-5 gap-y-3.5 max-sm:grid-cols-1">
          <!-- last_name (USER) -->
          <EditableField
            v-if="editMode && !lockUserFields"
            :label="$t('Фамилия')"
            required
            :model-value="data.last_name"
            @update:model-value="setField('last_name', $event)"
          />
          <ReadonlyField
            v-else
            :label="$t('Фамилия')"
            :value="data.last_name"
          />

          <!-- first_name (USER) -->
          <EditableField
            v-if="editMode && !lockUserFields"
            :label="$t('Имя')"
            required
            :model-value="data.first_name"
            @update:model-value="setField('first_name', $event)"
          />
          <ReadonlyField v-else :label="$t('Имя')" :value="data.first_name" />

          <!-- middle_name (USER) -->
          <EditableField
            v-if="editMode && !lockUserFields"
            :label="$t('Отчество')"
            :model-value="data.middle_name"
            @update:model-value="setField('middle_name', $event)"
          />
          <ReadonlyField
            v-else
            :label="$t('Отчество')"
            :value="data.middle_name"
          />

          <!-- identifier (USER) -->
          <EditableField
            v-if="editMode && !lockUserFields"
            :label="$t('ИИН')"
            required
            :model-value="data.identifier"
            @update:model-value="setField('identifier', $event)"
          />
          <ReadonlyField v-else :label="$t('ИИН')" :value="data.identifier" />

          <!-- previous_full_name (PROFILE) -->
          <EditableField
            v-if="editMode"
            :label="$t('Предыдущее ФИО')"
            full
            :model-value="data.previous_full_name"
            @update:model-value="setField('previous_full_name', $event)"
          />
          <ReadonlyField
            v-else
            :label="$t('Предыдущее ФИО')"
            full
            :value="data.previous_full_name"
          />
        </div>
      </Section>

      <Section plain :title="$t('Место рождения')">
        <div class="grid grid-cols-2 gap-x-5 gap-y-3.5 max-sm:grid-cols-1">
          <!-- place_of_birth_id (PROFILE) -->
          <EditableField
            v-if="editMode"
            :label="$t('ID места рождения')"
            type="number"
            :model-value="data.place_of_birth_id"
            @update:model-value="setField('place_of_birth_id', $event)"
          />
          <template v-else>
            <ReadonlyField
              :label="$t('Страна')"
              :value="data.birth_country || data.place_of_birth?.country"
            />
            <ReadonlyField
              :label="$t('Область')"
              :value="data.birth_region || data.place_of_birth?.region"
            />
          </template>
        </div>
      </Section>
    </div>

    <div class="flex flex-col">
      <Section
        plain
        narrow
        :title="$t('Контакты')"
        :sub="$t('Контактные данные')"
      >
        <div class="flex flex-col gap-3.5">
          <!-- email (USER) -->
          <EditableField
            v-if="editMode && !lockUserFields"
            label="Email"
            required
            :model-value="data.email"
            @update:model-value="setField('email', $event)"
          />
          <ReadonlyField v-else label="Email" :value="data.email" />

          <!-- phone (USER) -->
          <EditableField
            v-if="editMode && !lockUserFields"
            :label="$t('Контактный телефон')"
            required
            :model-value="data.phone"
            @update:model-value="setField('phone', $event)"
          />
          <ReadonlyField
            v-else
            :label="$t('Контактный телефон')"
            :value="data.phone"
          />

          <!-- work_phone (PROFILE) -->
          <EditableField
            v-if="editMode"
            :label="$t('Рабочий телефон')"
            :model-value="data.work_phone"
            @update:model-value="setField('work_phone', $event)"
          />
          <ReadonlyField
            v-else
            :label="$t('Рабочий телефон')"
            :value="data.work_phone"
          />
        </div>
      </Section>

      <Section plain narrow :title="$t('Организация и роль')">
        <div class="flex flex-col gap-3.5">
          <!-- organization_id (USER) -->
          <EditableField
            v-if="editMode && !lockUserFields"
            :label="$t('Организация')"
            required
            :options="
              organizations.map((o) => ({ value: o.id, label: o.name }))
            "
            :model-value="data.organization_id"
            @update:model-value="setField('organization_id', $event)"
          />
          <ReadonlyField
            v-else
            :label="$t('Организация')"
            :value="
              organizations.find(
                (o) => String(o.id) === String(data.organization_id)
              )?.name ||
              data.organization?.name ||
              data.organization?.short_name ||
              data.user?.organization?.name ||
              data.user?.organization?.short_name ||
              data.profile?.organization?.name ||
              data.organization_name
            "
          />

          <!-- status (PROFILE) -->
          <EditableField
            v-if="editMode"
            :label="$t('Статус')"
            :model-value="data.status"
            @update:model-value="setField('status', $event)"
          />
          <ReadonlyField v-else :label="$t('Статус')" :value="data.status" />

          <!-- registration_date (PROFILE) -->
          <EditableField
            v-if="editMode"
            :label="$t('Дата регистрации')"
            type="date"
            disable-future
            :model-value="data.registration_date"
            @update:model-value="setField('registration_date', $event)"
          />
          <ReadonlyField
            v-else
            :label="$t('Дата регистрации')"
            :value="data.registration_date"
            type="date"
            size="sm"
          />
        </div>
      </Section>
    </div>
  </div>
</template>
